import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { getSubContractors } from '../../../services/subContractorService';
import { getVendors } from '../../../services/vendorService';

/**
 * Multi-select dropdown for a single category (SC or Vendor).
 */
const MultiSelectDropdown = ({ label, icon, items, selectedIds, onChange, loading, emptyMessage }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleItem = (id) => {
    const updated = selectedIds?.includes(id)
      ? selectedIds?.filter(x => x !== id)
      : [...selectedIds, id];
    onChange(updated);
  };

  const removeItem = (id, e) => {
    e?.stopPropagation();
    onChange(selectedIds?.filter(x => x !== id));
  };

  const selectedItems = items?.filter(item => selectedIds?.includes(item?.id));

  return (
    <div className="space-y-1.5" ref={ref}>
      <label className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <Icon name={icon} size={14} className="text-accent" />
        {label}
        {selectedIds?.length > 0 && (
          <span className="ml-1 text-xs bg-accent/10 text-accent px-1.5 py-0.5 rounded-full font-medium">
            {selectedIds?.length}
          </span>
        )}
      </label>
      {/* Trigger button */}
      <div
        className="relative w-full min-h-[38px] px-3 py-2 rounded-md border border-border bg-background text-sm cursor-pointer focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary transition-colors"
        onClick={() => setOpen(prev => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e?.key === 'Enter' || e?.key === ' ') setOpen(prev => !prev); }}
      >
        <div className="flex flex-wrap gap-1 pr-6">
          {selectedItems?.length === 0 ? (
            <span className="text-muted-foreground">Select {label}...</span>
          ) : (
            selectedItems?.map(item => (
              <span
                key={item?.id}
                className="inline-flex items-center gap-1 bg-accent/10 text-accent text-xs px-2 py-0.5 rounded-full font-medium"
              >
                {item?.name}
                <button
                  type="button"
                  onClick={(e) => removeItem(item?.id, e)}
                  className="hover:text-accent/60 transition-colors"
                  aria-label={`Remove ${item?.name}`}
                >
                  <Icon name="X" size={10} />
                </button>
              </span>
            ))
          )}
        </div>
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={14} />
        </span>
      </div>
      {/* Dropdown list */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-4 text-muted-foreground text-xs gap-2">
              <Icon name="Loader2" size={14} className="animate-spin" />
              Loading...
            </div>
          ) : items?.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4 px-3">{emptyMessage}</p>
          ) : (
            items?.map(item => {
              const isSelected = selectedIds?.includes(item?.id);
              return (
                <div
                  key={item?.id}
                  onClick={() => toggleItem(item?.id)}
                  className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${
                    isSelected ? 'bg-accent/10' : 'hover:bg-muted/60'
                  }`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                    isSelected ? 'bg-accent border-accent' : 'border-border'
                  }`}>
                    {isSelected && <Icon name="Check" size={10} className="text-accent-foreground" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium truncate ${isSelected ? 'text-accent' : 'text-foreground'}`}>
                      {item?.name}
                    </p>
                    {item?.contactName && (
                      <p className="text-xs text-muted-foreground truncate">{item?.contactName}</p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Reusable multi-select panel for linking Sub-Contractors and Vendors.
 * Props:
 *   selectedSCIds: string[]
 *   selectedVendorIds: string[]
 *   onChange: ({ subContractorIds, vendorIds }) => void
 */
const ContractorsVendorsSelector = ({
  selectedSCIds = [],
  selectedVendorIds = [],
  onChange,
}) => {
  const [subContractors, setSubContractors] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [scs, vs] = await Promise.all([
          getSubContractors(),
          getVendors(),
        ]);
        setSubContractors(scs || []);
        setVendors(vs || []);
      } catch (err) {
        console.error('Error loading contractors/vendors:', err?.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="border border-border rounded-lg overflow-visible">
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b border-border">
        <Icon name="Users" size={14} className="text-accent" />
        <span className="text-sm font-medium text-foreground">Contractors &amp; Vendors</span>
      </div>
      <div className="p-3 space-y-4 relative">
        <MultiSelectDropdown
          label="Sub-Contractors"
          icon="Wrench"
          items={subContractors}
          selectedIds={selectedSCIds}
          onChange={(ids) => onChange({ subContractorIds: ids, vendorIds: selectedVendorIds })}
          loading={loading}
          emptyMessage="No sub-contractors available. Add them from the Team menu."
        />
        <MultiSelectDropdown
          label="Vendors"
          icon="ShoppingBag"
          items={vendors}
          selectedIds={selectedVendorIds}
          onChange={(ids) => onChange({ subContractorIds: selectedSCIds, vendorIds: ids })}
          loading={loading}
          emptyMessage="No vendors available. Add them from the Team menu."
        />
      </div>
    </div>
  );
};

export default ContractorsVendorsSelector;
